import { useMemo, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import {
  activeChild, activeCycle, focusTargets, reviewFor, weekBlocks, type PlanWeek,
} from '../../store/selectors'
import { domainColor, domainLabel } from '../../lib/rubric'
import { formatFriendlyDate } from '../../lib/dates'
import { Sheet } from '../ui/Sheet'

/**
 * The weekly check-in table, as it was on paper.
 *
 * One row per domain the week actually worked (plus the cycle's focus
 * domains): a rating — slipped, held, grew, leapt — with the week's block
 * record beside it so the rating is grounded, not guessed. Then the week's own
 * questions, the biggest win, and what to adjust. Submitting hands the week to
 * Aurora, which reviews it and reshapes next week around what you said.
 */

const RATINGS: Array<{ value: number; glyph: string; label: string }> = [
  { value: -1, glyph: '−', label: 'Slipped' },
  { value: 0, glyph: '=', label: 'Held' },
  { value: 1, glyph: '+', label: 'Grew' },
  { value: 2, glyph: '++', label: 'Leapt' },
]

export function WeekCheckin({
  monday, week, open, onClose,
}: { monday: string; week?: PlanWeek; open: boolean; onClose: () => void }) {
  const child = useAuroraStore(activeChild)
  const cycle = useAuroraStore(activeCycle) as any
  const targets = useAuroraStore(focusTargets)
  const days = useAuroraStore((s: any) => weekBlocks(s, monday))
  const existing = useAuroraStore((s: any) => reviewFor(s, 'week', monday))

  // Domains that were on the plan this week, most-worked first, then focus domains.
  const rows = useMemo(() => {
    const counts = new Map<string, { total: number; done: number; partial: number }>()
    for (const d of days) {
      for (const b of d.blocks) {
        const key = b.domain ?? 'other'
        const c = counts.get(key) ?? { total: 0, done: 0, partial: 0 }
        c.total++
        if (b.status === 'done') c.done++
        if (b.status === 'partial') c.partial++
        counts.set(key, c)
      }
    }
    for (const t of targets) if (t.domainKey && !counts.has(t.domainKey)) counts.set(t.domainKey, { total: 0, done: 0, partial: 0 })
    return [...counts.entries()]
      .sort((a, b) => b[1].total - a[1].total)
      .map(([key, c]) => ({ key, ...c }))
  }, [days, targets])

  const questions = week?.checkinQuestions ?? []
  const [ratings, setRatings] = useState<Record<string, number>>(() =>
    Object.fromEntries((existing?.parent?.ratings ?? []).map((r) => [r.domainKey, r.rating])),
  )
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries((existing?.parent?.answers ?? []).map((a) => [a.question, a.answer])),
  )
  const [win, setWin] = useState(existing?.parent?.win ?? '')
  const [adjust, setAdjust] = useState(existing?.parent?.adjust ?? '')
  const [notes, setNotes] = useState(existing?.parent?.notes ?? '')

  if (!child) return null

  const submit = () => {
    const s = auroraStore.getState() as any
    s.submitReview({
      childId: (child as any)._id,
      period: 'week',
      date: monday,
      weekId: week?.id,
      cycleId: cycle?._id,
      ratings: Object.entries(ratings).map(([domainKey, rating]) => ({ domainKey, rating })),
      answers: questions.map((q) => ({ question: q, answer: answers[q] ?? '' })).filter((a) => a.answer.trim()),
      win, adjust, notes,
    })
    onClose()
  }

  const rated = Object.keys(ratings).length
  const label = week ? `Week ${week.number}${week.theme ? ` · ${week.theme}` : ''}` : 'This week'

  return (
    <Sheet
      open={open}
      onClose={onClose}
      eyebrow="Weekly check-in"
      title={label}
      wide
      footer={
        <div className="row row-2" style={{ justifyContent: 'space-between', width: '100%' }}>
          <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>
            {rated ? `${rated} of ${rows.length} rated` : 'Rate what you can — blanks are fine'}
          </span>
          <button className="btn btn-primary" onClick={submit}>
            {existing?.parent?.submittedAt ? 'Update check-in' : 'Send to Aurora'}
          </button>
        </div>
      }
    >
      <p className="wk-lead">
        {formatFriendlyDate(monday, (child as any).timezone)} onward. For each area: did she slip, hold, grow, or leap this week?
        The block record sits beside each row so you are rating what actually happened.
      </p>

      <table className="wk-table">
        <thead>
          <tr><th>Area</th><th>This week</th><th>Rating</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} style={{ '--domain-color': domainColor(r.key) } as React.CSSProperties}>
              <td className="wk-domain"><span className="wk-swatch" aria-hidden="true" />{domainLabel(r.key)}</td>
              <td className="wk-record tnum">
                {r.total === 0
                  ? <span className="muted">not on the plan</span>
                  : <>{r.done}{r.partial ? `+${r.partial}½` : ''} of {r.total} done</>}
              </td>
              <td>
                <div className="wk-rating" role="radiogroup" aria-label={`Rating for ${domainLabel(r.key)}`}>
                  {RATINGS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={`wk-rate${ratings[r.key] === o.value ? ' is-active' : ''} wk-rate-${o.value < 0 ? 'neg' : o.value}`}
                      onClick={() =>
                        setRatings((prev) => {
                          const next = { ...prev }
                          if (next[r.key] === o.value) delete next[r.key]
                          else next[r.key] = o.value
                          return next
                        })
                      }
                      aria-pressed={ratings[r.key] === o.value}
                      title={o.label}
                    >
                      {o.glyph}
                    </button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {questions.length > 0 && (
        <div className="wk-questions">
          <h3 className="section-title">This week's questions</h3>
          {questions.map((q) => (
            <label key={q} className="field">
              <span className="field-label">{q}</span>
              <input
                className="field-input"
                value={answers[q] ?? ''}
                onChange={(e) => setAnswers((p) => ({ ...p, [q]: e.target.value }))}
                placeholder="Y / N / a number / a few words"
              />
            </label>
          ))}
        </div>
      )}

      <div className="wk-questions">
        <label className="field">
          <span className="field-label">Biggest win</span>
          <input className="field-input" value={win} onChange={(e) => setWin(e.target.value)} placeholder="The moment worth remembering" />
        </label>
        <label className="field">
          <span className="field-label">Adjust for next week</span>
          <input className="field-input" value={adjust} onChange={(e) => setAdjust(e.target.value)} placeholder="More of… less of… try instead…" />
        </label>
        <label className="field">
          <span className="field-label">Anything else</span>
          <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What she is into right now, what fell flat, what surprised you" />
        </label>
      </div>
    </Sheet>
  )
}
